/**
 * Shared semantic role maps.
 *
 * Single source of truth for every status/severity/health -> tone mapping in the app.
 * Before this file, 33 duplicated maps existed across features and disagreed with each
 * other (see .omo/plans/layout-hybrid-implementation.md §3.4). Anything that maps a
 * domain value to a visual tone MUST import from here.
 *
 * Tones are rendered by `StatusChip` / `Badge`; the class strings live in
 * `statusTone.ts` so both can share them.
 */

/** Visual tone vocabulary. `accent` is reserved for in-progress/selection, never for failure. */
export type Tone = 'ok' | 'warn' | 'danger' | 'info' | 'neutral' | 'accent';

/** Badge variant vocabulary (existing primitive, kept for compatibility). */
export type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';

export const toneToBadgeVariant: Record<Tone, BadgeVariant> = {
  ok: 'success',
  warn: 'warning',
  danger: 'error',
  info: 'info',
  neutral: 'neutral',
  accent: 'info',
};

/**
 * Job status -> tone.
 * Covers backup jobs, restore jobs, alert states and agent statuses.
 * Source of truth for the 13 duplicated `getStatusVariant` maps.
 */
const JOB_STATUS_TONES: Record<string, Tone> = {
  // Success
  COMPLETED: 'ok',
  RECOVERED: 'ok',
  SUCCESS: 'ok',
  OK: 'ok',
  HEALTHY: 'ok',
  ONLINE: 'ok',
  PAID: 'ok',
  ACTIVE: 'ok',
  // Failure
  FAILED: 'danger',
  ERROR: 'danger',
  QUOTA_EXCEEDED: 'danger',
  ALERTING: 'danger',
  UNHEALTHY: 'danger',
  CRITICAL: 'danger',
  REVOKED: 'danger',
  UNCOLLECTIBLE: 'danger',
  // Attention
  PENDING: 'warn',
  QUEUED: 'warn',
  STALE: 'warn',
  DEGRADED: 'warn',
  WARNING: 'warn',
  GRACE_PERIOD: 'warn',
  THROTTLED: 'warn',
  OPEN: 'warn',
  PAST_DUE: 'warn',
  // Breach workflow + notification delivery
  RESOLVED: 'ok',
  INVESTIGATING: 'warn',
  NOTIFYING_USERS: 'warn',
  SENT: 'ok',
  // In progress / informational
  RUNNING: 'info',
  VERIFYING: 'info',
  PROCESSING: 'info',
  IN_PROGRESS: 'info',
  CANCELLING: 'info',
  CANCELED: 'info',
  OFFLINE: 'neutral',
  VOID: 'neutral',
  INACTIVE: 'neutral',
  NONE: 'danger',
  EXPIRED: 'danger',
  TRIALING: 'info',
  ACTIVE_CANCELING: 'info',
  INCOMPLETE: 'warn',
  HARD_LIMIT: 'danger',
  EXCEEDED: 'danger',
};

/**
 * Resolve a job/health/agent status string to a tone.
 * Case-insensitive; unknown values fall back to `neutral`.
 */
export function getStatusTone(status: string | null | undefined): Tone {
  if (!status) return 'neutral';
  return JOB_STATUS_TONES[status.toUpperCase()] ?? 'neutral';
}

/**
 * Log level -> tone.
 * Accepts both `WARN` and `WARNING` (the app historically emitted both).
 */
const LOG_LEVEL_TONES: Record<string, Tone> = {
  ERROR: 'danger',
  ERR: 'danger',
  FATAL: 'danger',
  CRITICAL: 'danger',
  HIGH: 'warn',
  WARNING: 'warn',
  WARN: 'warn',
  MEDIUM: 'info',
  INFO: 'info',
  LOW: 'neutral',
  DEBUG: 'neutral',
  TRACE: 'neutral',
};

export function getLogLevelTone(level: string | null | undefined): Tone {
  if (!level) return 'neutral';
  return LOG_LEVEL_TONES[level.toUpperCase()] ?? 'neutral';
}

/**
 * Storage usage threshold -> tone.
 * Mirrors the backend contract used by `StorageBanner`.
 */
const STORAGE_THRESHOLD_TONES: Record<string, Tone> = {
  warning: 'warn',
  critical: 'danger',
  exceeded: 'danger',
  hard_limit: 'danger',
};

export function getStorageTone(status: string | null | undefined): Tone {
  if (!status) return 'neutral';
  return STORAGE_THRESHOLD_TONES[status.toLowerCase()] ?? 'neutral';
}

/**
 * Organization role -> tone.
 *
 * Resolves the §8.3 conflict between `TeamMembersContent.tsx` (OWNER=purple,
 * ADMIN=blue, MEMBER=green) and `Profile.tsx` (OWNER/ADMIN=info, OPERATOR=warn).
 * Contract chosen: privilege escalation is shown by *rank*, not by hue family:
 * owner > admin > operator > member. Colors carry no meaning beyond "higher rank
 * is more prominent", so `accent` for owner and `info` for admin are stable in
 * both themes and never collide with state colors (ok/warn/danger).
 */
const ROLE_TONES: Record<string, Tone> = {
  OWNER: 'accent',
  ADMIN: 'info',
  PLATFORM_ADMIN: 'accent',
  OPERATOR: 'warn',
  MEMBER: 'neutral',
  VIEWER: 'neutral',
};

export function getRoleTone(role: string | null | undefined): Tone {
  if (!role) return 'neutral';
  return ROLE_TONES[role.toUpperCase()] ?? 'neutral';
}

/**
 * Storage destination type -> tone.
 * Distinct hues per backend type so the icon is identifiable at a glance.
 */
const STORAGE_TYPE_TONES: Record<string, Tone> = {
  PLATFORM: 'accent',
  S3: 'warn',
  AZURE_BLOB: 'info',
  GCS: 'ok',
};

export function getStorageTypeTone(type: string | null | undefined): Tone {
  if (!type) return 'neutral';
  return STORAGE_TYPE_TONES[type.toUpperCase()] ?? 'neutral';
}

/**
 * Subscription status -> tone, plus the label key used by `SubscriptionBanner`.
 * `canDismiss` mirrors the current product rule (only non-critical states).
 */
const SUBSCRIPTION_TONES: Record<string, { tone: Tone; canDismiss: boolean }> = {
  NONE: { tone: 'danger', canDismiss: false },
  CANCELED: { tone: 'danger', canDismiss: false },
  INACTIVE: { tone: 'danger', canDismiss: false },
  EXPIRED: { tone: 'danger', canDismiss: false },
  PAST_DUE: { tone: 'warn', canDismiss: true },
  INCOMPLETE: { tone: 'warn', canDismiss: false },
  TRIALING: { tone: 'info', canDismiss: true },
  ACTIVE_CANCELING: { tone: 'info', canDismiss: true },
};

export function getSubscriptionTone(status: string | null | undefined): Tone {
  if (!status) return 'neutral';
  return SUBSCRIPTION_TONES[status.toUpperCase()]?.tone ?? 'neutral';
}

export function isSubscriptionDismissible(status: string | null | undefined): boolean {
  if (!status) return false;
  return SUBSCRIPTION_TONES[status.toUpperCase()]?.canDismiss ?? false;
}

/**
 * Verification series (charts). Fixed order so CPU/RAM/disk/IO keep their hue
 * across the dashboard and the metrics page.
 */
export const VIZ_SERIES = {
  cpu: 'viz-1',
  ram: 'viz-2',
  disk: 'viz-3',
  io: 'viz-4',
} as const;

export type VizSeries = keyof typeof VIZ_SERIES;
